import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import makeComponentTrashable from "trashable-react";
import { fetchHistoricalLedger } from "Shared/util/web-api";
import { RadioGroup, RadioButton } from "Shared/components/radio";
import { Select } from "Shared/components/select";
import months from "Shared/data/months";
import Page from "Shared/components/page/page";
import filterStyles from "Shared/components/list-filter.css";
import StatusMessage from "Shared/components/status-message";
import { exportPage } from "Shared/util/export-helper";
import ExportOptions from "Shared/components/export-options";
import { useStatusMessage, useTranslation } from "Shared/util/custom-hooks";
import { funcFilterStatus } from "Shared/util/translations";
import HistoricalLedgerFilter, * as filterInvoiceStates from "./historical-ledger-filter";
import BasicInvoiceTable from "../invoices/basic-invoice-table";
import {
    modifyInvoices,
    getDefaultYearAndMonth,
    getFilterYears,
    getFilterMonths,
} from "./historical-ledger-page-utils";
import InputCheckbox from "Shared/components/input-checkbox";

const HistoricalLedgerPage = ({ registerPromise }) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, dispatchStatusMessage] = useStatusMessage();
    const t = useTranslation((x) => x.invoices);
    const tg = useTranslation((x) => x.global);
    const distinctLedgerDates = useMemo(
        () => [...new Set(invoices.map((i) => new Date(i.ledgerDate)))],
        [invoices],
    );
    const [defaultYear, defaultMonth] = useMemo(
        () => getDefaultYearAndMonth(distinctLedgerDates),
        [distinctLedgerDates],
    );

    useEffect(() => {
        dispatchStatusMessage({ type: "NONE" });
        registerPromise(fetchHistoricalLedger()).then((result) => {
            setInvoices(result);
            setLoading(false);
        });
    }, []);

    return (
        <Page title={t.historicalLedgerTitle} loading={loading}>
            <HistoricalLedgerFilter
                default={{
                    invoiceState: filterInvoiceStates.ALL,
                    year: defaultYear,
                    month: defaultMonth,
                    showDeducted: false,
                }}
                items={invoices}
                dismissExportAlert={() =>
                    dispatchStatusMessage({ type: "NONE" })
                }
                render={(filterArgs) => (
                    <>
                        <div className={filterStyles.filterItemsContainer}>
                            <div
                                className={`${filterStyles.filterItem} ${filterStyles.filterSelectContainer}`}
                            >
                                <Select
                                    items={getFilterYears(distinctLedgerDates)}
                                    selectedValue={filterArgs.filter.year}
                                    onChange={(value) =>
                                        filterArgs.handleChange({ year: value })
                                    }
                                />
                                <Select
                                    items={getFilterMonths(
                                        distinctLedgerDates,
                                        filterArgs.filter.year,
                                        funcFilterStatus(months, tg, "label"),
                                    )}
                                    selectedValue={filterArgs.filter.month}
                                    onChange={(value) =>
                                        filterArgs.handleChange({
                                            month: value,
                                        })
                                    }
                                />
                                <InputCheckbox
                                    checked={filterArgs.filter.showDeducted}
                                    onChange={(value) =>
                                        filterArgs.handleChange({
                                            showDeducted: value.isChecked,
                                        })
                                    }
                                    label={tg.showDeducted_invoice}
                                />
                            </div>
                            <div className={filterStyles.filterItem}>
                                <RadioGroup
                                    name="ledger-filter"
                                    value={filterArgs.filter.invoiceState}
                                    handleChange={(e) =>
                                        filterArgs.handleChange({
                                            invoiceState: e.target.value,
                                        })
                                    }
                                >
                                    <RadioButton
                                        value={filterInvoiceStates.ALL}
                                        text={tg.all}
                                    />
                                    <RadioButton
                                        value={filterInvoiceStates.SOLD}
                                        text={tg.sold}
                                    />
                                    <RadioButton
                                        value={filterInvoiceStates.MONITORED}
                                        text={tg.monitored}
                                    />
                                </RadioGroup>
                            </div>
                        </div>
                        <ExportOptions
                            exportPage={exportPage.HISTORICALLEDGER}
                            filterArgs={filterArgs}
                            dispatchStatusMessage={dispatchStatusMessage}
                        />
                        <StatusMessage
                            status={statusMessage.type}
                            text={statusMessage.message}
                            clearStatus={() =>
                                dispatchStatusMessage({ type: "NONE" })
                            }
                        />
                        <BasicInvoiceTable
                            rows={modifyInvoices(
                                filterArgs.items,
                                filterArgs.filter.year,
                                filterArgs.filter.month,
                                filterArgs.filter.showDeducted,
                            )}
                        />
                    </>
                )}
            />
        </Page>
    );
};

HistoricalLedgerPage.propTypes = {
    registerPromise: PropTypes.func.isRequired,
};

export default makeComponentTrashable(HistoricalLedgerPage);
